/**
 * Main Billing Contact dropdown + Additional Billing Contacts checklist,
 * used on the new-invoice and edit-invoice pages (Oct 2026). Options come
 * from the supplier's contacts. The person picked as Main is left out of
 * the checklist. Names saved on an invoice that are no longer on the
 * supplier stay visible so they can be kept or unticked.
 */
export default function BillingContactsPicker({
  contacts,
  mainName,
  additionalNames,
  onChange,
  disabled = false,
}) {
  const list = (Array.isArray(contacts) ? contacts : []).filter((c) =>
    String(c?.name || '').trim(),
  );
  const main = String(mainName || '').trim();
  const extra = Array.isArray(additionalNames) ? additionalNames : [];
  const lower = (s) => String(s || '').trim().toLowerCase();
  const onFile = new Set(list.map((c) => lower(c.name)));

  const boxStyle = {
    padding: '8px 10px',
    background: '#fafafa',
    border: '1px solid var(--brand-border, #e5e7eb)',
    borderRadius: 6,
    fontSize: 13,
  };

  if (list.length === 0 && !main && extra.length === 0) {
    return (
      <div className="field full">
        <label>Main Billing Contact</label>
        <div className="muted" style={boxStyle}>
          No contacts on file. Add them on the Supplier page.
        </div>
      </div>
    );
  }

  const setMain = (name) => {
    onChange({
      mainName: name,
      additionalNames: extra.filter((n) => lower(n) !== lower(name)),
    });
  };
  const toggleExtra = (name) => {
    const has = extra.some((n) => lower(n) === lower(name));
    onChange({
      mainName: main,
      additionalNames: has
        ? extra.filter((n) => lower(n) !== lower(name))
        : [...extra, name],
    });
  };

  const extraOptions = [
    ...list.map((c) => ({
      name: String(c.name).trim(),
      detail: [c.title, c.email].map((v) => String(v || '').trim()).filter(Boolean).join(' · '),
    })),
    ...extra
      .filter((n) => !onFile.has(lower(n)))
      .map((n) => ({ name: n, detail: 'No longer on the supplier' })),
  ].filter((o) => lower(o.name) !== lower(main));

  return (
    <>
      <div className="field">
        <label>
          Main Billing Contact.{' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            Name appears on invoice.
          </span>
        </label>
        <select value={main} onChange={(e) => setMain(e.target.value)} disabled={disabled}>
          <option value="">— None —</option>
          {main && !onFile.has(lower(main)) && <option value={main}>{main}</option>}
          {list.map((c, idx) => {
            const n = String(c.name).trim();
            return (
              <option key={`${n}-${idx}`} value={n}>
                {c.title ? `${n} — ${c.title}` : n}
              </option>
            );
          })}
        </select>
      </div>
      <div className="field full">
        <label>
          Additional Billing Contacts.{' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            Receive emailed copies only. Names do not appear on invoice.
          </span>
        </label>
        {extraOptions.length === 0 ? (
          <div className="muted" style={boxStyle}>
            No other contacts on file for this supplier.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid var(--brand-border, #e5e7eb)',
              borderRadius: 6,
              padding: 8,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 6,
            }}
          >
            {extraOptions.map((o) => {
              const checked = extra.some((n) => lower(n) === lower(o.name));
              return (
                <label
                  key={o.name}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 400,
                    cursor: disabled ? 'default' : 'pointer',
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleExtra(o.name)}
                    disabled={disabled}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    {o.name}
                    {o.detail && (
                      <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                        {o.detail}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
